import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { Persona } from '../types/persona.type'
import { BadgeEditorService, updateBadgeResponse } from '@app/modules/admin/components/badge-editor/services/badge-editor.service'

@Injectable()
export class PersonaBadgePreviewService {
  private static readonly _BADGE_DATA_ELEMENTS_TO_ALWAYS_KEEP = new Set(['persona_name', 'qr_code', 'SPACER'])
  private static readonly _PERSONA_NAME_DATA_ELEMENT = 'persona_name'

  constructor(private readonly _badgeEditorService: BadgeEditorService) {}

  /**
   * UI preview only: replace persona name then autosize; no persist.
   * @param badgeHtml {string} - Badge HTML string returned by API.
   * @param personaName {string} - Persona name to replace in the badge HTML.
   * @returns {string} - Updated badge HTML string.
   */
  public renderBadgePreviewHtml(badgeHtml: string, personaName: string): string {
    const parsed = this._tryParseBadgeHtml(badgeHtml)
    if (!parsed) return badgeHtml || ''
    return this._applyPersonaNameAndAutosize(parsed, personaName)
  }

  /**
   * Sync persona name inside badge HTML and persist it.
   * @param persona {Persona} - Persona with `badge.badge_front_html_content` to update.
   * @returns {Observable<updateBadgeResponse>} - Badge update response from API, or a no-op observable when no badge exists.
   */
  public syncBadgePersonaName(persona: Persona): Observable<updateBadgeResponse> {
    const currentBadgeHtml = persona.badge?.badge_front_html_content
    if (!currentBadgeHtml) return of({ badge: persona.badge } as updateBadgeResponse)

    const parsed = this._tryParseBadgeHtml(currentBadgeHtml)
    if (!parsed) return of({ badge: persona.badge } as updateBadgeResponse)

    const updatedBadgeHtml = this._applyPersonaNameAndAutosize(parsed, persona.name)
    if (updatedBadgeHtml === currentBadgeHtml) return of({ badge: persona.badge } as updateBadgeResponse)

    return this._badgeEditorService.updateBadge({ form_id: persona.id, badge_front_html_content: updatedBadgeHtml, reset: false })
  }

  /**
   * Sync a badge html string with latest persona form elements.
   * - Remove badge slots whose `[data-element]` not in `persona.form_elements[].name`
   * - Update `.draggable-column[key]` to match `form_elements[].id` for remaining fields
   * Returns updated badge outerHTML, or null when parsing fails.
   */
  public syncBadgeHtmlFromFormElements(persona: { form_elements?: Array<{ id?: number | string; name?: string | null } | null> | null }, badgeHtml: string): string | null {
    if (!badgeHtml) return null
    const doc = new DOMParser().parseFromString(badgeHtml, 'text/html')
    const badgeRoot = doc.getElementById('badge') as HTMLDivElement | null
    if (!badgeRoot) return null

    const fieldNameToId = new Map<string, string>()
    for (const el of persona?.form_elements ?? []) {
      if (!el) continue
      const name = typeof el.name === 'string' ? el.name.trim() : ''
      if (!name) continue
      const id = el.id
      if (id == null || String(id).trim() === '') continue
      fieldNameToId.set(name, String(id))
    }

    const validFieldNames = new Set(fieldNameToId.keys())
    const allDataElementNames = new Set(
      Array.from(badgeRoot.querySelectorAll<HTMLElement>('[data-element]'))
        .map((el) => el.getAttribute('data-element'))
        .filter((v): v is string => typeof v === 'string' && v.length > 0)
    )

    const fieldsToDelete: string[] = []
    allDataElementNames.forEach((fieldName) => {
      if (PersonaBadgePreviewService._BADGE_DATA_ELEMENTS_TO_ALWAYS_KEEP.has(fieldName)) return
      if (!validFieldNames.has(fieldName)) fieldsToDelete.push(fieldName)
    })

    this._deleteBadgeFields(badgeRoot, fieldsToDelete)

    validFieldNames.forEach((fieldName) => {
      if (PersonaBadgePreviewService._BADGE_DATA_ELEMENTS_TO_ALWAYS_KEEP.has(fieldName)) return
      const elementId = fieldNameToId.get(fieldName)
      if (!elementId) return
      const nodes = Array.from(badgeRoot.querySelectorAll<HTMLElement>(`[data-element="${CSS.escape(fieldName)}"]`))
      nodes.forEach((node) => {
        const col = node.closest<HTMLElement>('.draggable-column')
        if (!col) return
        if (col.getAttribute('key') !== elementId) col.setAttribute('key', elementId)
      })
    })

    return badgeRoot.outerHTML
  }

  /**
   * Parse badge HTML and extract required layout metadata.
   * @param badgeHtml {string} - Badge HTML string returned by API.
   * @returns {{ badgeRoot: HTMLDivElement; badgeWidthMM: number; badgeHeightMM: number } | null}
   */
  private _tryParseBadgeHtml(badgeHtml: string): { badgeRoot: HTMLDivElement; badgeWidthMM: number; badgeHeightMM: number } | null {
    if (!badgeHtml) return null
    const doc = new DOMParser().parseFromString(badgeHtml, 'text/html')
    const badgeRoot = doc.getElementById('badge') as HTMLDivElement | null
    if (!badgeRoot) return null
    const badgeWidthMM = parseFloat(badgeRoot.getAttribute('data-width') || '0')
    const badgeHeightMM = parseFloat(badgeRoot.getAttribute('data-height') || '0')
    if (!badgeWidthMM || !badgeHeightMM) return null
    return { badgeRoot, badgeWidthMM, badgeHeightMM }
  }

  /**
   * Apply persona name and autosize to the badge HTML.
   * @param parsed {Object} - Parsed badge HTML.
   * @param personaName {string} - Persona name to replace in the badge HTML.
   * @returns {string} - Updated badge HTML string.
   */
  private _applyPersonaNameAndAutosize(parsed: { badgeRoot: HTMLDivElement; badgeWidthMM: number; badgeHeightMM: number }, personaName: string): string {
    const { badgeRoot, badgeWidthMM, badgeHeightMM } = parsed
    const displayName = String(personaName ?? '')
      .replace(/&nbsp;/g, '')
      .toUpperCase()

    const nameNodes = Array.from(badgeRoot.querySelectorAll<HTMLElement>(`[data-element="${PersonaBadgePreviewService._PERSONA_NAME_DATA_ELEMENT}"]`))
    if (!nameNodes.length) return badgeRoot.outerHTML

    nameNodes.forEach((el) => (el.innerHTML = `&nbsp;${displayName}&nbsp;`))
    return this._applyPersonaNameAutoSize(badgeRoot, nameNodes, badgeWidthMM, badgeHeightMM)
  }

  /**
   * Apply an auto-sized font-size to persona-name elements by measuring layout offscreen.
   * @param badgeRoot {HTMLDivElement} - Root `#badge` element (detached).
   * @param personaNameElements {HTMLElement[]} - Persona-name nodes (children of badgeRoot).
   * @param badgeWidthMM {number} - Badge width in millimeters (`data-width`).
   * @param badgeHeightMM {number} - Badge height in millimeters (`data-height`).
   * @returns {string} - Updated badge outerHTML.
   */
  private _applyPersonaNameAutoSize(badgeRoot: HTMLDivElement, personaNameElements: HTMLElement[], badgeWidthMM: number, badgeHeightMM: number): string {
    const measurementHost = document.createElement('div')
    measurementHost.style.position = 'absolute'
    measurementHost.style.left = '-99999px'
    measurementHost.style.top = '-99999px'
    measurementHost.style.width = `${badgeWidthMM}mm`
    measurementHost.style.height = `${badgeHeightMM}mm`
    measurementHost.style.overflow = 'hidden'
    measurementHost.style.zIndex = '-999'

    document.body.appendChild(measurementHost)
    measurementHost.appendChild(badgeRoot)

    try {
      personaNameElements.forEach((node) => {
        const p = node as HTMLParagraphElement
        const parent = p.parentElement
        if (!parent) return

        p.style.fontSize = '1px'
        const widthRatio = parent.clientWidth / p.clientWidth
        const heightRatio = parent.clientHeight / p.clientHeight
        const ratio = Math.min(widthRatio, heightRatio)
        const base = parseInt(p.style.fontSize)
        p.style.fontSize = base * ratio + 'px'
      })
      return badgeRoot.outerHTML
    } finally {
      if (measurementHost.parentElement === document.body) document.body.removeChild(measurementHost)
    }
  }

  private _deleteBadgeFields(badgeRoot: HTMLElement, fieldsToDelete: string[]): void {
    fieldsToDelete.forEach((key) => {
      const elements = badgeRoot.querySelectorAll(`[data-element="${CSS.escape(key)}"]`)
      if (!elements?.length) return
      elements.forEach((element) => this._removeElementAndCleanupParents(element))
    })
  }

  private _removeElementAndCleanupParents(element: Element): void {
    const column = element.closest('.draggable-column')
    if (!column) return

    const container = column.closest('.columns-container')
    if (!container) return

    column.remove()

    if (!container.querySelector('.draggable-column')) {
      this._removeContainerAndRow(container)
    }
  }

  private _removeContainerAndRow(container: Element): void {
    const row = container.closest('[id^="row-"]')
    if (!row) return

    container.remove()

    if (!row.querySelector('.columns-container')) {
      row.remove()
    }
  }
}
